import React, { Component } from 'react'
import { inject, observer } from 'mobx-react';
import { observable, toJS } from 'mobx';
import { Alert, Intent } from '@blueprintjs/core';
import { currencyFormattedAmount } from 'helpers/currency';
import Pagination from 'components/Pagination';
import Footer from 'components/Footer';
import Toast from 'helpers/toaster';
import * as DataList from 'components/DataList';

// Layout shared by the list and its loading skeleton (pages/pricing_parameters/index.js).
export const PRICING_PARAMETER_COLUMNS = [
  { area: 'name', label: 'Name', width: 'minmax(170px, 1.2fr)', bone: 'single' },
  { area: 'price', label: 'Price', width: 'minmax(150px, 1.1fr)' },
  { area: 'range', label: 'Range', width: 'minmax(110px, 0.8fr)', bone: 'single', icon: 'arrows-horizontal' },
  { area: 'duration', label: 'Duration', width: '100px', bone: 'single', icon: 'time' },
  { area: 'services', label: 'Service types', width: 'minmax(170px, 1.2fr)', bone: 'single', icon: 'tag' },
  { area: 'actions', label: '', width: '104px', bone: 'none' },
];

export const PRICING_PARAMETER_CARDS = {
  columns: 'minmax(0, 1fr) auto',
  areas: [
    'name actions',
    'price price',
    'range range',
    'duration duration',
    'services services',
  ],
};

export const PRICING_PARAMETER_MIN_TABLE_WIDTH = 980;

const hasValue = (value) => value !== null && value !== undefined && value !== '';

// Price cell: one price, or (for range pricing) "N price ranges" over each
// range's label and price.
const priceSummary = (attributes) => {
  const ranges = attributes.pricing_parameter_ranges || [];
  if (attributes.pricing_type === 'range') {
    if (ranges.length === 0) return null;
    return {
      primary: `${ranges.length} price ${ranges.length === 1 ? 'range' : 'ranges'}`,
      secondary: ranges.map((range) => `${range.label}: ${currencyFormattedAmount(range.price)}`).join(', '),
    };
  }
  if (!hasValue(attributes.price)) return null;
  return { primary: currencyFormattedAmount(attributes.price) };
};

// Range cell: the parameter's own start-end, or each price range's min-max.
const rangeSummary = (attributes) => {
  const ranges = attributes.pricing_parameter_ranges || [];
  if (attributes.range_start === null) {
    const parts = ranges
      .filter((range) => hasValue(range.min_range) || hasValue(range.max_range))
      .map((range) => `${range.min_range} – ${range.max_range}`);
    return parts.length > 0 ? parts.join(', ') : null;
  }
  if (!hasValue(attributes.range_start) && !hasValue(attributes.range_end)) return null;
  return `${attributes.range_start} – ${attributes.range_end}`;
};

// Duration cell: "Varies" when the price ranges carry their own ranges.
const durationSummary = (attributes) => {
  const ranges = attributes.pricing_parameter_ranges || [];
  if (ranges.length > 0 && ranges[0].min_range != null) return 'Varies';
  return hasValue(attributes.duration) ? String(attributes.duration) : null;
};

@inject('store', 'clientStore', 'pricingParameterStore', 'viewStore') @observer
class PricingParametersTable extends Component {
  @observable isOpenAddEquipmentDialog = false;
  @observable isDeleteDialogVisible = false;
  @observable pricing;

  onPaginationChange(newPage) {
    let {
      viewStore: {
        pricingParameterSearch
      }
    } = this.props;
    this.props.viewStore.pricingParameterSearch = {
      ...pricingParameterSearch,
      page: newPage
    };
    this.props.viewStore.pricingParameters();
  }

  handleDeleteCancel() {
    this.isDeleteDialogVisible = false;
  }

  handleDeleteConfirm() {
    this.isDeleteDialogVisible = false;
    this.props.pricingParameterStore.deletePricingParameter(this.pricing)
      .then(this.onSuccessfulDeletePricingParameter.bind(this))
      .catch(this.onFailDeletePricingParameter.bind(this))
  }

  onSuccessfulDeletePricingParameter(response) {
    Toast.show({ icon: 'thumbs-up', message: 'Pricing parameter deleted successfully!', intent: Intent.SUCCESS })
    this.service = null;
    this.isDeleteDialogVisible = false;
  }

  onFailDeletePricingParameter(error) {
    this.isDeleteDialogVisible = false;
    Toast.show({ icon: 'warning-sign', message: 'Unable to delete pricing parameter!', intent: Intent.DANGER })
  }

  handleServiceTypeClick(event, pricing) {
    switch (event.label) {
      case 'Edit':
        this.pricing = pricing;
        this.props.viewStore.pricingParameterShow({uuid: pricing.attributes.id})
        break;
      case 'Delete':
        this.isDeleteDialogVisible = true;
        this.pricing = pricing;
        break;
      default:
    }
  }

  copyPricingParameter(pricing) {
    let {
      props: {
        pricingParameterStore: {
          pricingParameterDetail,
          pricingParameterDetail: { 
            object
           }
        }
      }
    } = this;

    pricingParameterDetail.object = toJS(pricing); // a copy, so the row itself is untouched
    pricingParameterDetail.object.id = '';
    pricingParameterDetail.object.pricing_parameter_services_attributes = [];
    pricingParameterDetail.object.pricing_parameter_ranges_attributes = [];
    if(pricingParameterDetail.object.pricing_parameter_services){
      pricingParameterDetail.object.pricing_parameter_services && pricingParameterDetail.object.pricing_parameter_services.map((values) => {
        return(
          pricingParameterDetail.object.pricing_parameter_services_attributes.push({
            service_type_id:values.service_type_id,
            label:values.label
          })
        )
      })
    }
    pricingParameterDetail.object.pricing_parameter_ranges_attributes = [];
    if(pricingParameterDetail.object.pricing_parameter_ranges){
      pricingParameterDetail.object.pricing_parameter_ranges && pricingParameterDetail.object.pricing_parameter_ranges.map((values) => {
        return(
          pricingParameterDetail.object.pricing_parameter_ranges_attributes.push({
            label:values.label,
            min_range:values.min_range,
            max_range:values.max_range,
            price:values.price,
          })
        )
      })
    }

    this.props.pricingParameterStore
      .createPricingParameter()
      .then(response => {
        Toast.show({
          icon: "thumbs-up",
          message: "Pricing parameter copied successfully!",
          intent: Intent.SUCCESS
        });
      })
      .catch(error => {
        Toast.show({
          icon: "warning-sign",
          message: "Unable to copied pricing parameter!",
          intent: Intent.DANGER
        });
      });
  }

  // Empty list's "Clear search": back to all pricing parameters, page 1.
  clearSearch() {
    const { viewStore } = this.props;
    viewStore.pricingParameterSearch = { ...viewStore.pricingParameterSearch, term: '*', page: 1 };
    viewStore.bumpListFilters();
    viewStore.pricingParameters();
  }

  render() {
    let {
      viewStore,
      pricingParameterStore: {
        pricingParameter: {
          objects,
          meta
        }
      }
    } = this.props;
    const { pricingParameterSearch } = viewStore;
    const term = pricingParameterSearch.term && pricingParameterSearch.term !== '*' ? pricingParameterSearch.term : '';

    if (objects.length === 0) {
      return (
        <DataList.Empty
          noun="pricing parameters"
          term={term}
          onClearFilters={this.clearSearch.bind(this)}
          extraAction={!term && { label: 'Add pricing parameter', icon: 'plus', onClick: () => viewStore.pricingParameterAdd(), primary: true }}
        />
      );
    }
    return (
      <React.Fragment>
        <Footer>
          <div className="bp3-navbar-group bp3-align-left">
            <Pagination
              value={meta.page}
              total={meta.total_pages}
              onChange={this.onPaginationChange.bind(this)}
            />
          </div>
        </Footer>
        <DataList.List
          columns={PRICING_PARAMETER_COLUMNS}
          cards={PRICING_PARAMETER_CARDS}
          minTableWidth={PRICING_PARAMETER_MIN_TABLE_WIDTH}
          total={meta.total_records || objects.length}
          noun="pricing parameters"
          note={DataList.sortNote(pricingParameterSearch.order)}
        >
          {objects.map((pricings) => {
            const attributes = pricings.attributes;
            const price = priceSummary(attributes);
            const range = rangeSummary(attributes);
            const duration = durationSummary(attributes);
            const serviceNames = (attributes.service_types || []).map((service) => service.name).filter(Boolean);
            return (
              <DataList.Row key={pricings.id}>
                <DataList.Cell area="name">
                  <DataList.Lines
                    title={attributes.name}
                    primary={
                      <DataList.RowLink
                        href={`/dashboard/pricing-parameter/${attributes.id}/show`}
                        label={`Pricing parameter ${attributes.name}`}
                      >
                        {attributes.name}
                      </DataList.RowLink>
                    }
                  />
                </DataList.Cell>
                <DataList.Cell area="price">
                  {price ? (
                    <DataList.Lines primary={price.primary} secondary={price.secondary} strong={false} />
                  ) : (
                    <DataList.Muted>—</DataList.Muted>
                  )}
                </DataList.Cell>
                <DataList.Cell area="range">
                  {range ? (
                    <DataList.Lines primary={range} strong={false} />
                  ) : (
                    <DataList.Muted>—</DataList.Muted>
                  )}
                </DataList.Cell>
                <DataList.Cell area="duration">
                  {duration ? (
                    <DataList.Lines primary={duration} strong={false} />
                  ) : (
                    <DataList.Muted>—</DataList.Muted>
                  )}
                </DataList.Cell>
                <DataList.Cell area="services">
                  {serviceNames.length > 0 ? (
                    <DataList.Lines primary={serviceNames.join(', ')} strong={false} />
                  ) : (
                    <DataList.Muted>No service types</DataList.Muted>
                  )}
                </DataList.Cell>
                <DataList.Cell area="actions">
                  <DataList.Actions
                    items={[
                      { icon: 'duplicate', label: 'Duplicate pricing parameter', onClick: () => this.copyPricingParameter(attributes) },
                      { icon: 'edit', label: 'Edit pricing parameter', onClick: () => viewStore.pricingParameterShow({ uuid: attributes.id }) },
                      { icon: 'trash', label: 'Delete pricing parameter', intent: 'danger', onClick: () => this.handleServiceTypeClick({ label: 'Delete' }, pricings) },
                    ]}
                  />
                </DataList.Cell>
              </DataList.Row>
            );
          })}
        </DataList.List>
        <Alert
          cancelButtonText="Cancel"
          confirmButtonText="Delete"
          icon="trash"
          intent={Intent.DANGER}
          isOpen={this.isDeleteDialogVisible}
          onCancel={this.handleDeleteCancel.bind(this)}
          onConfirm={this.handleDeleteConfirm.bind(this)}
        >
          <p>Warnning! Deleting this service type will not be recoverable.</p>
          <p>Are you sure you want to do this?</p>
        </Alert>
        <Footer>
          <div className="bp3-navbar-group bp3-align-left">
            <Pagination
              value={meta.page}
              total={meta.total_pages}
              onChange={this.onPaginationChange.bind(this)}
            />
          </div>
        </Footer>
      </React.Fragment>
    )
  }

}

export default PricingParametersTable;